import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenImageViewer from "./FullscreenImageViewer";
import { useLanding } from "../context/LandingContext";
import { resolveLandingMedia, handleLandingMediaError } from "../utils/landingMedia";
import { normalizeImagePosition } from "../utils/segmentVisualStyle";
import { capitalizeSectionTitle } from "../utils/landingSectionText";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    const handleChange = (event) => setPrefersReducedMotion(event.matches);
    setPrefersReducedMotion(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(handleChange);
      return () => {
        if (typeof mediaQuery.removeListener === "function") mediaQuery.removeListener(handleChange);
      };
    }

    return undefined;
  }, []);

  return prefersReducedMotion;
}

function bounded(value, fallback, min, max) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

function normalizeSliderAnimation(value) {
  const normalized = String(value || "slide").trim().toLowerCase();
  return ["slide", "fade", "zoom", "none"].includes(normalized) ? normalized : "slide";
}

export default function LandingImageCarouselSection({ sectionDomId = "imageCarousel" }) {
  const { imageCarousel = {} } = useLanding();
  const items = useMemo(
    () => (Array.isArray(imageCarousel.items) ? imageCarousel.items : [])
      .map((item, index) => ({ ...item, sourceIndex: index, imageUrl: resolveLandingMedia(item?.imageUrl) }))
      .filter((item) => item.imageUrl || item.caption),
    [imageCarousel.items]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [viewerState, setViewerState] = useState(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const viewerItems = useMemo(() => items.filter((item) => Boolean(item.imageUrl)), [items]);
  const openViewer = () => {
    if (!items[activeIndex]?.imageUrl) return;
    setViewerState({
      images: viewerItems.map((item) => ({
        src: item.imageUrl,
        alt: item.imageAlt || item.caption || "",
        caption: item.caption || "",
        sourceIndex: item.sourceIndex,
      })),
      initialIndex: Math.max(0, viewerItems.findIndex((item) => item.sourceIndex === items[activeIndex]?.sourceIndex)),
    });
  };

  useEffect(() => setActiveIndex((current) => Math.min(current, Math.max(0, items.length - 1))), [items.length]);
  useEffect(() => {
    if (viewerState || imageCarousel.autoplay === false || items.length < 2 || prefersReducedMotion) return undefined;
    const interval = bounded(imageCarousel.intervalMs, 4500, 2000, 15000);
    const timer = window.setInterval(() => {
      setDirection(1);
      setActiveIndex((current) => (current + 1) % items.length);
    }, interval);
    return () => window.clearInterval(timer);
  }, [imageCarousel.autoplay, imageCarousel.intervalMs, items.length, prefersReducedMotion, viewerState]);

  if (!items.length && !imageCarousel.eyebrow && !imageCarousel.title && !imageCarousel.subtitle) return null;

  const active = items[activeIndex] || null;
  const shellStyle = imageCarousel.shellStyle || {};
  const eyebrowStyle = imageCarousel.eyebrowStyle || {};
  const titleStyle = imageCarousel.titleStyle || {};
  const subtitleStyle = imageCarousel.subtitleStyle || {};
  const mediaStyle = imageCarousel.mediaStyle || {};
  const captionStyle = imageCarousel.captionStyle || {};
  const arrowStyle = imageCarousel.arrowStyle || {};
  const dotStyle = imageCarousel.dotStyle || {};
  const sliderAnimation = normalizeSliderAnimation(imageCarousel.animation);
  const move = (step) => {
    setDirection(step < 0 ? -1 : 1);
    setActiveIndex((current) => (current + step + items.length) % items.length);
  };
  const goTo = (nextIndex) => {
    if (nextIndex === activeIndex) return;
    setDirection(nextIndex < activeIndex ? -1 : 1);
    setActiveIndex(nextIndex);
  };
  const mediaAnimation = prefersReducedMotion || sliderAnimation === "none"
    ? "none"
    : sliderAnimation === "fade"
      ? "landingCarouselFadeIn 420ms ease both"
      : sliderAnimation === "zoom"
        ? "landingCarouselZoomIn 520ms ease-out both"
        : direction < 0
          ? "landingCarouselSlideFromLeft 480ms cubic-bezier(.2,.75,.2,1) both"
          : "landingCarouselSlideFromRight 480ms cubic-bezier(.2,.75,.2,1) both";

  const arrowSx = {
    position: "absolute", top: "50%", transform: "translateY(-50%)",
    width: bounded(arrowStyle.size, 40, 28, 80), height: bounded(arrowStyle.size, 40, 28, 80),
    bgcolor: arrowStyle.backgroundColor || "rgba(255,255,255,.92)", color: arrowStyle.color || "#19242F",
    border: `1px solid ${arrowStyle.borderColor || "transparent"}`, boxShadow: arrowStyle.boxShadow || "none",
    transition: "background-color .16s ease,color .16s ease,border-color .16s ease,box-shadow .16s ease",
    "&:hover": { bgcolor: arrowStyle.hoverBackgroundColor || "#FFFFFF", color: arrowStyle.hoverColor || "#19242F", borderColor: arrowStyle.hoverBorderColor || "transparent" },
    "&.Mui-focusVisible": { outline: `2px solid ${arrowStyle.focusColor || "#F35A3A"}`, outlineOffset: 2 },
  };

  return (
    <Box component="section" id={sectionDomId} data-landing-section="imageCarousel" sx={{ px: { xs: 2, sm: 3, md: 6 }, py: { xs: 4.5, md: 8 }, bgcolor: shellStyle.backgroundColor || "#FFFFFF" }}>
      <Box sx={{ width: "100%", maxWidth: 1320, mx: "auto" }}>
        {(imageCarousel.eyebrow || imageCarousel.title || imageCarousel.subtitle) ? (
          <Stack spacing={0.7} sx={{ mb: items.length ? 2.5 : 0 }}>
            {imageCarousel.eyebrow ? <Typography variant="overline" sx={{ color: eyebrowStyle.color || "#F35A3A", fontSize: bounded(eyebrowStyle.fontSize, 12, 8, 96), fontWeight: bounded(eyebrowStyle.fontWeight, 900, 100, 900), lineHeight: bounded(eyebrowStyle.lineHeight, 1.4, .6, 4), letterSpacing: bounded(eyebrowStyle.letterSpacing, 1.2, -8, 24) }}>{imageCarousel.eyebrow}</Typography> : null}
            {imageCarousel.title ? <Typography sx={{ color: titleStyle.color || "#19242F", fontSize: { xs: bounded(titleStyle.fontSizeMobile, 30, 8, 96), md: bounded(titleStyle.fontSizeDesktop, 40, 8, 120) }, lineHeight: bounded(titleStyle.lineHeight, 1.08, .6, 4), letterSpacing: bounded(titleStyle.letterSpacing, -.4, -8, 24), fontWeight: bounded(titleStyle.fontWeight, 900, 100, 900), overflowWrap: "normal", wordBreak: "normal", hyphens: "none" }}>{capitalizeSectionTitle(imageCarousel.title)}</Typography> : null}
            {imageCarousel.subtitle ? <Typography sx={{ color: subtitleStyle.color || "#5B5D73", fontSize: bounded(subtitleStyle.fontSize, 16, 8, 96), fontWeight: bounded(subtitleStyle.fontWeight, 400, 100, 900), maxWidth: bounded(subtitleStyle.maxWidth, 780, 120, 1800), lineHeight: bounded(subtitleStyle.lineHeight, 1.65, .6, 4) }}>{imageCarousel.subtitle}</Typography> : null}
          </Stack>
        ) : null}

        {active ? (
          <Box sx={{ position: "relative", height: { xs: bounded(mediaStyle.heightMobile, 280, 180, 640), md: bounded(mediaStyle.heightDesktop, 460, 220, 800) }, overflow: "hidden", borderRadius: `${bounded(mediaStyle.borderRadius, 28, 0, 160)}px`, bgcolor: mediaStyle.backgroundColor || "rgba(25,36,47,.06)", border: `${bounded(mediaStyle.borderWidth, 0, 0, 16)}px solid ${mediaStyle.borderColor || "transparent"}`, boxShadow: mediaStyle.boxShadow || "none", "@keyframes landingCarouselFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } }, "@keyframes landingCarouselZoomIn": { from: { opacity: 0, transform: "scale(1.035)" }, to: { opacity: 1, transform: "scale(1)" } }, "@keyframes landingCarouselSlideFromLeft": { from: { opacity: .72, transform: "translateX(-3%)" }, to: { opacity: 1, transform: "translateX(0)" } }, "@keyframes landingCarouselSlideFromRight": { from: { opacity: .72, transform: "translateX(3%)" }, to: { opacity: 1, transform: "translateX(0)" } } }}>
            <Box key={`${active.id || active.sourceIndex}:${activeIndex}`} sx={{ position: "absolute", inset: 0, animation: mediaAnimation, willChange: sliderAnimation === "none" ? "auto" : "transform, opacity" }}>
              {active.imageUrl ? (
                <Box component="button" type="button" aria-label={`Ampliar imagen ${activeIndex + 1} en pantalla completa`} onClick={openViewer}
                  sx={{ position: "absolute", inset: 0, display: "block", width: "100%", height: "100%", p: 0, border: 0, bgcolor: "transparent", cursor: "zoom-in", "&:focus-visible": { outline: "3px solid #FFFFFF", outlineOffset: -5 } }}>
                  <Box component="img" src={active.imageUrl} alt={active.imageAlt || active.caption || ""} decoding="async" onError={handleLandingMediaError} sx={{ width: "100%", height: "100%", display: "block", objectFit: "cover", objectPosition: normalizeImagePosition(active.imagePosition) }} />
                </Box>
              ) : null}
              {active.imageUrl ? (
                <Box aria-hidden="true" sx={{ position: "absolute", right: { xs: 12, md: 18 }, top: { xs: 12, md: 18 }, display: "flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: "50%", bgcolor: "rgba(0,0,0,.52)", color: "#fff", pointerEvents: "none" }}>
                  <FullscreenRoundedIcon />
                </Box>
              ) : null}
              {active.caption ? <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", p: { xs: 2.2, md: 3.5 }, background: `linear-gradient(180deg, transparent 35%, ${captionStyle.overlayColor || "rgba(0,0,0,.38)"} 100%)`, pointerEvents: "none" }}><Typography sx={{ color: captionStyle.color || "#FFFFFF", fontSize: { xs: bounded(captionStyle.fontSizeMobile, 17, 8, 96), md: bounded(captionStyle.fontSizeDesktop, 22, 8, 120) }, lineHeight: bounded(captionStyle.lineHeight, 1.35, .6, 4), fontWeight: bounded(captionStyle.fontWeight, 800, 100, 900), maxWidth: bounded(captionStyle.maxWidth, 900, 120, 1800) }}>{active.caption}</Typography></Box> : null}
            </Box>
            {imageCarousel.showArrows !== false && items.length > 1 ? <><IconButton aria-label="Imagen anterior" onClick={() => move(-1)} sx={{ ...arrowSx, left: { xs: 10, md: 18 } }}><ChevronLeftRoundedIcon /></IconButton><IconButton aria-label="Imagen siguiente" onClick={() => move(1)} sx={{ ...arrowSx, right: { xs: 10, md: 18 } }}><ChevronRightRoundedIcon /></IconButton></> : null}
            {imageCarousel.showDots !== false && items.length > 1 ? <Stack direction="row" spacing={0.7} sx={{ position: "absolute", left: "50%", bottom: { xs: 12, md: 16 }, transform: "translateX(-50%)", alignItems: "center" }}>{items.map((item, index) => { const activeDot = index === activeIndex; return <Box key={item.id || item.sourceIndex} component="button" type="button" aria-label={`Mostrar imagen ${index + 1}`} aria-current={activeDot ? "true" : undefined} onClick={() => goTo(index)} sx={{ width: activeDot ? bounded(dotStyle.activeWidth, 22, 2, 96) : bounded(dotStyle.width, 8, 2, 64), height: bounded(dotStyle.height, 8, 2, 32), p: 0, border: 0, borderRadius: 999, cursor: "pointer", bgcolor: activeDot ? (dotStyle.activeColor || "#FFFFFF") : (dotStyle.color || "rgba(255,255,255,.55)"), transition: "width .2s ease,background-color .16s ease", "&:hover": { bgcolor: activeDot ? (dotStyle.activeHoverColor || "#FFFFFF") : (dotStyle.hoverColor || "#FFFFFF") } }} />; })}</Stack> : null}
          </Box>
        ) : null}
      </Box>
      <FullscreenImageViewer
        open={Boolean(viewerState)}
        images={viewerState?.images || []}
        initialIndex={viewerState?.initialIndex || 0}
        title={imageCarousel.title || "Galería de imágenes"}
        onClose={() => setViewerState(null)}
        onImageError={handleLandingMediaError}
        onImageChange={(photo) => {
          const next = items.findIndex((item) => item.sourceIndex === photo.sourceIndex);
          if (next >= 0) goTo(next);
        }}
      />
    </Box>
  );
}
